import '../index.css';

import { Box, Button, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  addEdge,
  updateEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useViewport,
} from 'reactflow';
import PremiumToolIcon from '../assets/icons/PremiumToolIcon.svg';
import ModalComponent from '../components/Modals';
import PopupWithVideo from '../components/PopupWithVideo/PopupWithVideo';
import ReactFlowBody from '../components/ReactFlow/ReactFlowBody';
import RemainingTokens from '../components/RemainingTokens/RemainingTokens';
import SavingStatus from '../components/SavingStatus/SavingStatus';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import PaywallModal from '../components/UI/PaywallModal';
import { coreEdgeStyle } from '../components/edges/coreEdgeStyleDef';
import { AuthService } from '../service/AuthService';
import { FlowService } from '../service/FlowService';
import { UserService } from '../service/UserService';
import { useAuthStore } from '../store/storeAuth';
import { useDeprecatedNodesStore } from '../store/storeDeprecatedNodes';
import { checkDeprecatedModels } from '../util/checkDeprecatedModels';
import useStore from '../store/storeFlows';
import { useNodesStore } from '../store/storeNodesEdeges';
import { useNotificationStore } from '../store/storeNotifications';
import { useObjectStore } from '../store/storeObjects';
import { useSessionStore } from '../store/storeSessions';
import { handleChanges } from '../util/autosave/changeTypes';
import { shouldSyncNodesBoxTextFromSession } from '../util/shouldSyncNodesBoxTextFromSession';
import { nodeDataAutoSaveDynamic } from '../util/autosave/nodedata_autosave';
import { checkHandleConnectable } from '../util/checkHandlesConnectable';
import { getNodePositionInsideParent } from '../util/getNodePositionInsideParent';
import {
  getEdgeType,
  isNodeOverlapping,
  nodeSchema,
  onAddNodeFc,
  onConnectFc,
} from '../util/myFunctionsCoreDisplay';
import { getNextPlanAtLeastProfessional } from '../util/planUtil';
import { useDebounceEffect } from '../util/useDebounceEffect';
import Sidebar from './Sidebar';

import ContextMenu, { ContextMenuProps } from '../components/ContextMenu';
import type { VersionItem } from '../components/Layout/VersionControlDropdown';
import VersionNameModal from '../components/Layout/VersionNameModal';
import LoopDetectionWrapper from '../components/LoopDetectionWrapper';
import { ExternalFileDragIndicator } from '../components/ReactFlow/ExternalFileDragIndicator';
import { PdfOptionsModal } from '../components/ReactFlow/PdfOptionsModal';
import { useExternalFileDrop } from '../hooks/useExternalFileDrop';
import {
  useCreateVersion,
  useDeployedVersion,
  usePushToProduction,
  useRollback,
  useVersionHistory,
  useVersions,
} from '../hooks/useFlowVersions';
import { useVersionControlStore } from '../store/storeVersionControl';
import type {
  FlowVersionSummaryDto,
  VersionHistoryEntryDto,
} from '../types/FlowVersion';

const CoreNodeElement = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const edgeUpdateSuccessful = useRef(true);
  const {
    setStateNodes,
    setEdgeState,
    setSchema,
    setSaving,
    setOnEdgeDelete,
    setFlowId,
    flowId: activeFlowId,
    spaceId,
    setSpaceId,
    setIsPublic,
    setIsWebhook,
    clearWaypoints,
    beginFlowCanvasLoad,
    setFlowCanvasPhase,
    flowCanvasPhase,
  } = useStore(state => state);

  const [isLoading, setIsLoading] = useState(false);
  const { configs } = useAuthStore(state => state);
  const [openPaywall, setOpenPaywall] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const setNotification = useNotificationStore(state => state.setNotification);

  const { setViewport, screenToFlowPosition } = useReactFlow();
  const navigate = useNavigate();
  const { deleteNode, setDeleteNode } = useNodesStore(state => state);
  const [shouldBackgroundClick, setShouldBackgroundClick] = useState(true);
  const { user } = useAuthStore(state => state);
  const { clearSessionStore, setSessions } = useSessionStore(state => state);
  const { objects, setObjects, updateObject } = useObjectStore(state => state);

  const searchParams = useSearchParams();
  const [showOnboardingVideo, setShowOnboardingVideo] = useState(
    searchParams[0].get('showVideo') === '1',
  );
  const params = useParams();
  const { flowId } = params;
  const position = useViewport();

  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [type, setType] = useState<string>('paste');
  // const [type, setType] = useState<string>('paste');

  const [isModalOpen, setIsModalOPen] = useState(false);
  const nextPlanAtLeastProfessional = useMemo(
    () => getNextPlanAtLeastProfessional(user?.plan || null),
    [user?.plan],
  );
  const [updateNodeEdge, setUpdateNodeEdge] = useState<{
    type: string | null;
    id: string | null;
    data: {
      x: number;
      y: number;
    } | null;
    initial: boolean;
  }>({
    type: null,
    id: null,
    data: null,
    initial: true,
  });
  const [menu, setMenu] = useState<Partial<ContextMenuProps>>({});

  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [flowMetadata, setFlowMetadata] = useState<any>(null);
  const urlVersionId =
    new URLSearchParams(window.location.search).get('versionId') || '';
  const [selectedVersionId, setSelectedVersionId] =
    useState<string>(urlVersionId);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showVersionNameModal, setShowVersionNameModal] = useState(false);
  const [historyVersionId, setHistoryVersionId] = useState<string>('');
  const versionControlEnabled = flowMetadata?.versionControlEnabled || false;

  const { data: deployedVersion } = useDeployedVersion(
    flowId!,
    versionControlEnabled,
  );
  const { data: allVersions } = useVersions(
    flowId!,
    1,
    50,
    versionControlEnabled,
  );
  const { data: versionHistory } = useVersionHistory(
    flowId!,
    historyVersionId,
    1,
    50,
    showVersionHistory && !!historyVersionId,
  );

  const isProductionView =
    versionControlEnabled && !!deployedVersion && !urlVersionId;

  const createVersionMutation = useCreateVersion();
  const pushToProductionMutation = usePushToProduction();
  const rollbackMutation = useRollback();

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
    setShouldBackgroundClick(false);
  }, []);

  const onEdgeDelete = useCallback(
    async (edge: Edge[], id: string) => {
      if (!flowId) {
        return;
      }

      const edgesToRemoveIds = edge.map(edg => edg.id);
      const newEdgesAfterDelete = edges.filter(
        e => !edgesToRemoveIds.includes(e.id),
      );

      for (const edg of edge) {
        clearWaypoints(edg.id);
        try {
          await FlowService.deleteEdge({
            flowId,
            edgeId: edg.id,
            versionId: urlVersionId || undefined,
          });

          setEdges(edges => {
            const previous = edges || [];

            const newEdges = previous.filter(
              edge => !edgesToRemoveIds.includes(edge.id),
            );

            return newEdges;
          });
        } catch {
          setNotification({
            type: 'error',
            message: 'Failed to delete edge, please try again later.',
          });

          return;
        }
      }

      nodeDataAutoSaveDynamic({
        setNodeState: setNodes,
        id: edge?.[0].source,
        flowId: flowId!,
        changeType: 'delete-edges',
        setSchema,
        setSaving,
        newEdges: newEdgesAfterDelete,
        deleteEdges: edge,
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],
        versionId: urlVersionId || undefined,
      });
    },
    [
      setEdges,
      setNodes,
      user,
      flowId,
      edges,
      clearWaypoints,
      setSchema,
      setSaving,
      setNotification,
      urlVersionId,
    ],
  );

  useEffect(() => {
    if (!setNodes || !setEdges) {
      return;
    }
    setStateNodes(setNodes);
    setEdgeState(setEdges);
    setOnEdgeDelete(onEdgeDelete as never);
  }, [
    setNodes,
    setEdges,
    setStateNodes,
    setEdgeState,
    setOnEdgeDelete,
    onEdgeDelete,
  ]);

  const prevIsLoadingRef = useRef(false);

  useEffect(() => {
    return () => {
      useStore.getState().setFlowCanvasPhase('idle');
    };
  }, []);

  useEffect(() => {
    if (!flowId || !user?.id) {
      useStore.getState().setFlowCanvasPhase('idle');
    }
  }, [flowId, user?.id]);

  useEffect(() => {
    if (isLoading) {
      prevIsLoadingRef.current = true;
      return;
    }
    if (!prevIsLoadingRef.current) {
      return;
    }
    prevIsLoadingRef.current = false;
    if (!flowId) {
      return;
    }
    setFlowCanvasPhase('hydrating');
    const gen = useStore.getState().flowLoadGeneration;
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        if (useStore.getState().flowLoadGeneration !== gen) {
          return;
        }
        setFlowCanvasPhase('ready');
      });
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf) {
        cancelAnimationFrame(innerRaf);
      }
    };
  }, [isLoading, flowId, setFlowCanvasPhase]);

  useEffect(() => {
    if (flowCanvasPhase !== 'ready') {
      return;
    }
    const unlock = () => {
      useStore.getState().unlockFlowCanvasUserGesture();
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
  }, [flowCanvasPhase]);

  useEffect(() => {
    const getFlow = async () => {
      beginFlowCanvasLoad();
      setIsLoading(true);

      setNodes([]);
      setEdges([]);
      clearSessionStore();

      setSaving({
        finished: true,
        status: '',
        flowId: params.flowId!,
      });

      try {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const historyId = urlSearchParams.get('historyId') || undefined;
        const versionId = urlSearchParams.get('versionId') || undefined;
        const response = await FlowService.getFlow(
          params.flowId!,
          undefined,
          historyId,
          versionId,
        );

        setFlowMetadata(response?.data);
        const edges = response?.data?.edges;
        setFlowId(params.flowId!);

        const filterDuplicates = (arr: any[]) => {
          return arr.filter(
            (item, index, self) =>
              index ===
              self.findIndex(
                t =>
                  t.source === item.source &&
                  t.sourceHandle === item.sourceHandle &&
                  t.target === item.target &&
                  t.targetHandle === item.targetHandle,
              ),
          );
        };

        const filteredEdges = filterDuplicates(edges);

        if (edges?.length !== filteredEdges?.length) {
          await FlowService.updateFlow(params.flowId!, {
            edges: filteredEdges,
          });
        }

        const flowSpaceId = response?.data?.spaceId;
        setSpaceId(flowSpaceId);
        setIsPublic(
          response?.data?.public === null ? false : response?.data?.public,
        );

        setIsWebhook(response?.data?.webhook);
        let nodes = response.data.nodes?.filter(
          (nd: any) =>
            nd?.type !== 'webNews' &&
            nd?.type !== 'webLocation' &&
            nd?.type !== 'webMedia',
        );

        const { technologies, companySpaceTechnologies } =
          useAuthStore.getState();
        const { setDeprecatedNodeIds, clearDeprecatedNodeIds } =
          useDeprecatedNodesStore.getState();

        clearDeprecatedNodeIds();

        const { nodes: migratedNodes, deprecatedNodeIds } =
          checkDeprecatedModels(
            nodes || [],
            technologies,
            companySpaceTechnologies,
            flowSpaceId,
          );
        nodes = migratedNodes;

        if (deprecatedNodeIds.size > 0) {
          setDeprecatedNodeIds(deprecatedNodeIds);
          for (const nodeId of deprecatedNodeIds) {
            const migratedNode = nodes.find((n: any) => n.id === nodeId);
            if (migratedNode) {
              console.info(
                `[DeprecatedModelMigration] Node ${nodeId} migrated`,
                migratedNode.data,
              );
              FlowService.updateNodeData(params.flowId!, {
                id: nodeId,
                data: migratedNode.data,
                changeType: 'default',
              });
            }
          }
        }

        const currentSpaceId = flowSpaceId || 'user';
        try {
          const userResponse = await AuthService.getMe();
          setObjects(userResponse.data.objects, 'user');

          const spacesResponse = await UserService.getSpaces();
          spacesResponse.data.forEach((space: any) => {
            setObjects(space.objects, space.id);
          });

          const objectCallerNodes = nodes.filter(
            (nd: any) => nd.type === 'nodesBox',
          );
          const processedObjectIds = new Set<string>();

          for (const node of objectCallerNodes) {
            const { selectedId, sessionEnabled, selectedSession } =
              node.data || {};

            if (
              !selectedId ||
              !sessionEnabled ||
              processedObjectIds.has(selectedId)
            ) {
              continue;
            }

            processedObjectIds.add(selectedId);

            const allObjects =
              currentSpaceId === 'user'
                ? userResponse.data.objects
                : spacesResponse.data.find((s: any) => s.id === currentSpaceId)
                  ?.objects || [];

            const object = allObjects?.find(
              (obj: any) => obj?.id === selectedId,
            );

            if (object && object.sessions && object.sessions.length > 0) {
              const targetSession =
                selectedSession || object.selectedSession || 'default';
              const sessionContent = object.sessions.find(
                (s: any) => s.id === targetSession,
              )?.content;

              if (
                shouldSyncNodesBoxTextFromSession(
                  object.text,
                  sessionContent,
                )
              ) {
                updateObject(
                  object.id,
                  {
                    ...object,
                    text: sessionContent,
                    selectedSession: targetSession,
                  },
                  currentSpaceId,
                );
              }
            }
          }
        } catch (objError) {
          console.log('Error loading objects:', objError);
        }

        const cleanedEdges = filterDuplicates(edges).map(edg => {
          const type =
            edg.type === 'loopEdge'
              ? 'loopEdge'
              : getEdgeType(edg.sourceHandle || '');
          return {
            ...edg,
            type,
            markerEnd: type === 'custom' ? undefined : edg.markerEnd,
          };
        });

        setEdges(cleanedEdges);
        setNodes(nodes);

        const globalStarterNode = nodes.find(
          (x: any) => x.type === 'globalStarter',
        );
        const sessions = globalStarterNode?.data?.sessions;
        if (sessions && sessions.length > 0) {
          setSessions(sessions);
        }

        setSaving({
          finished: true,
          status: 'saved',
          lastSaved:
            response?.data?.lastSaved ||
            response?.data?.updatedAt ||
            new Date().toISOString(),
          flowId: params.flowId!,
        });
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.id && params.flowId) {
      getFlow();
    }
  }, [params.flowId, user?.id, beginFlowCanvasLoad]);

  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      let newEdges: Edge[] = [];
      setEdges(els => {
        newEdges = [...updateEdge(oldEdge, newConnection, els)];
        return updateEdge(oldEdge, newConnection, els);
      });

      nodeDataAutoSaveDynamic({
        setNodeState: setNodes,
        id: '',
        flowId: flowId!,
        changeType: 'update-edge',
        setSchema,
        setSaving,
        newEdges,
        updateEdge: {
          oldEdge,
          newConnection,
        },
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],
        versionId: urlVersionId || undefined,
      });

      const onUpdateEdge = async () => {
        try {
          await FlowService.updateEdge(flowId!, {
            id: oldEdge.id,
            ...newConnection,
            versionId: urlVersionId || undefined,
          });

          return true;
        } catch (error) {
          setNotification({
            type: 'error',
            message: 'Failed to update edge please try again latter',
          });
          return false;
        }
      };

      edgeUpdateSuccessful.current = true;
      onUpdateEdge();
    },
    [edges, nodes, setEdges, setNodes, flowId, user],
  );
  const onEdgeUpdateEnd = useCallback(
    (_: MouseEvent | TouchEvent, edge: { id: string }) => {
      setTimeout(() => {
        setShouldBackgroundClick(true);
      }, 700);

      if (!edgeUpdateSuccessful.current) {
        const deleteEdge = async () => {
          try {
            await FlowService.deleteEdge({
              flowId: flowId!,
              edgeId: edge.id,
              versionId: urlVersionId || undefined,
            });

            return true;
          } catch (error) {
            setNotification({
              type: 'error',
              message: 'Failed to delete edge please try again latter',
            });
            return false;
          }
        };

        deleteEdge();

        nodeDataAutoSaveDynamic({
          setNodeState: setNodes,
          id: '',
          flowId: flowId!,
          changeType: 'delete-edges',
          setSchema,
          setSaving,
          newEdges: edges.filter(e => e.id !== edge.id),
          deleteEdges: edges.filter(e => e.id === edge.id),
          objectCallerData: [
            ...(user?.spaceObjects || []),
            ...(user?.objects || []),
          ],
          versionId: urlVersionId || undefined,
        });

        edgeUpdateSuccessful.current = true;

        setEdges(eds => eds.filter(e => e.id !== edge.id));
      }
    },
    [setEdges, setNodes, edgeUpdateSuccessful.current, edges, flowId, user],
  );

  const excludedIdPrefix = 'flow-global-starter';

  const onConnect = useCallback(
    (connectionParams: {
      target: string | string[];
      source: string | string[];
      sourceHandle: string;
      targetHandle: string;
    }) => {
      setShouldBackgroundClick(false);
      onConnectFc(
        setDeleteNode,
        connectionParams,
        setSchema,
        setEdges,
        edges,
        addEdge,
        coreEdgeStyle,
        setNodes,
        nodes,
        flowId!,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
        setSaving,
        setNotification,
        urlVersionId || undefined,
      );
    },
    [edges, nodes, setEdges, setNodes, user, flowId],
  );

  const onAddNode = useCallback(
    (type: {
      type: string;
      label: {
        label: string;
        color: string;
      };
      myType: string;
      title: string;
      position: { x: number; y: number };
      extent: string | undefined;
      parentNode: string | undefined;
      data?: any;
    }) => {
      onAddNodeFc(
        type,
        setNodes,
        setViewport,
        setEdges,
        position,
        configs,
        flowId!,
        setNotification,
        urlVersionId || undefined,
      );
    },
    [setNodes, setViewport, position, configs, flowId],
  );

  const {
    externalDragState,
    onDragOver: onDragOverExternal,
    onDragLeave: onDragLeaveExternal,
    onDrop: onDropExternal,
    onPaste: onPasteExternal,
    fileModalState,
    handleFileExtract,
    handleFileView,
    handleFileModalClose,
  } = useExternalFileDrop({
    reactFlowInstance,
    reactFlowWrapper,
    onAddNode,
    setNotification,
    spaceId,
    flowId,
  });

  const onGenerateApi = useMemo(() => {
    const getAllVarInputNodes = () => {
      const varInputNodes =
        nodes?.filter(node => node?.type === 'varInputNode') ?? [];

      if (!varInputNodes?.length) {
        return 'No var input nodes found';
      }

      return JSON.stringify(
        varInputNodes.map((node, idx) => ({
          inputId: node?.id,
          inputText: `Example  ${idx + 1} Text For Input `,
        })),
      );
    };

    return getAllVarInputNodes() ?? 'No var input nodes found';
  }, [nodes]);

  useEffect(() => {
    if (!shouldBackgroundClick) {
      setTimeout(() => {
        setShouldBackgroundClick(true);
      }, 1100);
    }
  }, [shouldBackgroundClick]);

  const handleDeleteNodes = useCallback(
    (nodesToDelete: Node[]) => {
      if (nodesToDelete.length === 0) return;

      const nodeIds = nodesToDelete.map(node => node.id);
      const nodesToDeleteData = nodesToDelete;

      const nodesGroupParentIds = nodesToDeleteData
        .filter((node: { type?: string }) => node.type === 'nodesGroup')
        .map(node => node.id);

      let workingNodes = nodes;
      if (nodesGroupParentIds.length > 0) {
        workingNodes = nodes.filter(
          n => !nodesGroupParentIds.includes(n.parentNode || ''),
        );
      }

      const nodeIdsSet = new Set(nodeIds);
      const edgesToDelete = edges.filter(
        e => nodeIdsSet.has(e.source) || nodeIdsSet.has(e.target),
      );
      const remainingEdges = edges.filter(
        e => !nodeIdsSet.has(e.source) && !nodeIdsSet.has(e.target),
      );

      const { nds } = handleChanges({
        node: workingNodes,
        changeType: 'delete-edges',
        id: '',
        objectData: {},
        edges: remainingEdges,
        deleteEdges: edgesToDelete,
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],
      });

      const validResult = nds.filter(
        (n: Node) => n !== undefined && n !== null,
      );
      const finalNodes = validResult.filter(n => !nodeIdsSet.has(n.id));

      nodeSchema(finalNodes, setSchema);
      const prevNodes = nodes;
      const prevEdges = edges;

      setNodes(finalNodes);
      setEdges(remainingEdges);

      void (async () => {
        const results = await Promise.allSettled(
          nodeIds.map(nodeId =>
            FlowService.deleteNode({
              flowId: flowId!,
              nodeId,
              versionId: urlVersionId || undefined,
            }),
          ),
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          setNodes(prevNodes);
          setEdges(prevEdges);
          setNotification({
            message: `Failed to delete ${failed.length} node(s). Please try again.`,
            type: 'error',
          });
          return;
        }
        setNotification({
          message: `Successfully deleted ${nodeIds.length} node(s)`,
          type: 'success',
        });
      })();
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      flowId,
      user,
      setSchema,
      setNotification,
      urlVersionId,
    ],
  );

  useDebounceEffect(
    () => {
      const deletedId = deleteNode.id;
      if (!deletedId) {
        return;
      }
      const nodesGroup: Node | undefined = nodes.find(
        node => node.id === deletedId,
      );
      const onDeleteNode = () => {
        const workingNodes = nodesGroup
          ? nodes.filter(node => node.parentNode !== nodesGroup.id)
          : nodes;

        const snapshotEdges = edges.filter(
          e => e.source !== deletedId && e.target !== deletedId,
        );
        const deleteEdgeList = edges.filter(
          edge => edge.source === deletedId || edge.target === deletedId,
        );

        const { nds } = handleChanges({
          node: workingNodes,
          changeType: 'delete-edges',
          id: '',
          objectData: {},
          edges: snapshotEdges,
          deleteEdges: deleteEdgeList,
          objectCallerData: [
            ...(user?.spaceObjects || []),
            ...(user?.objects || []),
          ],
        });

        const validResult = nds.filter(
          (n: Node) => n !== undefined && n !== null,
        );
        const withoutDeleted = validResult.filter(n => n.id !== deletedId);

        nodeSchema(withoutDeleted, setSchema);
        const prevNodes = nodes;
        const prevEdges = edges;

        setNodes(withoutDeleted);
        setEdges(snapshotEdges);

        void (async () => {
          try {
            await FlowService.deleteNode({
              flowId: flowId!,
              nodeId: deletedId,
              versionId: urlVersionId || undefined,
            });
          } catch {
            setNotification({
              message: 'Failed to delete node try again',
              type: 'error',
            });
            setNodes(prevNodes);
            setEdges(prevEdges);
          }
        })();
      };

      if (nodesGroup?.data?.isEmptyState) {
        onDeleteNode();
      } else {
        const result = window.confirm(
          'Are you sure you want to delete this node?',
        );

        if (result === true) {
          onDeleteNode();
        }
      }
      setDeleteNode(null);
    },
    [
      deleteNode?.id,
      setEdges,
      setNodes,
      flowId,
      user,
      urlVersionId,
      setNotification,
      setSchema,
    ],
    0,
  );

  useDebounceEffect(
    () => {
      const check = updateNodeEdge.data;
      if (
        updateNodeEdge.type &&
        updateNodeEdge.id &&
        check &&
        updateNodeEdge.initial === false
      ) {
        const updateNode = async () => {
          const saving = {
            finished: false,
            lastSaved: new Date().toISOString(),
            status: 'loading',
            flowId: flowId!,
          };

          setSaving(saving);
          try {
            let findNode = {
              ...nodes.find(node => node.id === updateNodeEdge.id),
            };

            if (findNode.data) {
              delete findNode.data;
            }

            await FlowService.updateNode(flowId!, {
              id: updateNodeEdge.id,
              ...findNode,
              versionId: urlVersionId || undefined,
            });

            const saving = {
              finished: true,
              lastSaved: new Date().toISOString(),
              status: 'saved',
              flowId: flowId!,
            };
            setSaving(saving);
            setUpdateNodeEdge({
              type: null,
              id: null,
              data: null,
              initial: false,
            });
          } catch (error) {
            setNotification({
              type: 'error',
              message: 'Failed to update the node',
            });

            const saving = {
              finished: false,
              lastSaved: new Date().toISOString(),
              status: 'error',
              flowId: flowId!,
            };

            setSaving(saving);
          }
        };

        updateNode();
      }
    },
    [updateNodeEdge],
    300,
  );

  const isValidConnection = useCallback(
    (params: Connection) => {
      return checkHandleConnectable(params, edges);
    },
    [nodes, edges],
  );

  const onDrop = useCallback(
    async (event: any) => {
      const wasExternalFileDrop = await onDropExternal(event);
      if (wasExternalFileDrop) return;
      const reactFlowBounds =
        reactFlowWrapper?.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      if (!type) return;
      if (
        (type.includes('apiCaller') || type.includes('mcpNode')) &&
        (user?.plan === 'Intro' || user?.plan === 'Starter')
      ) {
        setOpenPaywall(true);
      } else {
        const parseType = JSON?.parse(type);
        const position = reactFlowInstance?.project({
          x: event.clientX - (reactFlowBounds?.left || 0),
          y: event.clientY - (reactFlowBounds?.top || 0),
        });

        const intersections = reactFlowInstance
          ?.getIntersectingNodes({
            x: position?.x,
            y: position?.y,
            width: 40,
            height: 40,
          })
          .filter((n: { type: string }) => n.type === 'nodesGroup');

        const groupNode = intersections[0];

        let newType: any;
        if (parseType.type === 'libraryNode') {
          const { libraryId, flowId, libraryImageUrl, libraryScope, ...rest } =
            parseType;
          newType = {
            ...rest,
            position,
            data: {
              libraryId,
              flowId,
              libraryImageUrl,
              libraryScope,
            },
          };
        } else {
          newType = {
            ...parseType,
            position,
          };
        }

        if (groupNode) {
          newType.position = getNodePositionInsideParent(
            {
              position,
              width: 40,
              height: 40,
            },
            groupNode,
          ) ?? { x: 0, y: 0 };
          newType.parentNode = groupNode?.id;
          newType.extent = groupNode ? 'parent' : undefined;
        }
        onAddNode(newType);
      }
    },
    [
      onDropExternal,
      reactFlowInstance,
      reactFlowWrapper,
      user?.plan,
      onAddNode,
      setOpenPaywall,
    ],
  );

  const onClickAddNode = useCallback(
    (data: { type: string; label: string; store: any;[key: string]: any }) => {
      if (
        (data.type === 'apiCaller' || data.type === 'mcpNode') &&
        (user?.plan === 'Intro' || user?.plan === 'Starter')
      ) {
        setOpenPaywall(true);
      } else {
        const {
          height,
          width,
          transform: [transformX, transformY, zoomLevel],
        } = data?.store;

        const zoomMultiplier = 1 / zoomLevel;

        const centerX =
          -transformX * zoomMultiplier + (width * zoomMultiplier) / 2;
        const centerY =
          -transformY * zoomMultiplier + (height * zoomMultiplier) / 2;

        const nodeWidthOffset = 80 / 2;
        const nodeHeightOffset = 80 / 2;

        const { store, type: _type, label: _label, ...restData } = data;
        const type = {
          type: data.type,
          label: data.label,
          position: {
            x: centerX - nodeWidthOffset,
            y: centerY - nodeHeightOffset,
          },
          data: {
            libraryId: data.libraryId,
            flowId: data.flowId,
            libraryImageUrl: data.libraryImageUrl,
            ...restData,
          },
        };

        onAddNode(type as any);
      }
    },
    [onAddNode, user?.plan, params.flowId, position.x, position.y],
  );

  const handleSelectedNodeChange = (node: any) => {
    setSelectedNode(node);
  };

  // console.log('selectedNode', selectedNode);

  const onContextMenu = (event: any) => {
    const target = event.target as HTMLElement;

    // role 'presentation' is used to prevent the default context menu from appearing'

    const isModal = target.getAttribute('role') === 'presentation';
    const isInputOrTextarea =
      target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    if (isModal || isInputOrTextarea) {
      setMenu({});
      return;
    } else {
      event.preventDefault();

      const isNodeClicked = event.target.closest('.react-flow__node');

      if (!isNodeClicked && selectedNode?.id) {
        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        setMenu({
          id: selectedNode.id,
          top: position.y,
          left: position.x,
          type: 'paste',
        });
      }
    }
  };

  const onConnectBs = useCallback(
    (connection: Connection) => {
      setEdges(oldEdges => addEdge(connection, oldEdges));
    },
    [setEdges],
  );

  const handlePaste = useCallback(
    (event?: any) => {
      if (!event) {
        setNotification({
          message: 'Invalid event or missing clientX/clientY.',
          type: 'error',
        });

        return;
      }

      // console.log('selectedNode handlePaste', selectedNode);
      if (selectedNode) {
        // Check if the node ID starts with the excluded prefix
        if (selectedNode.id && selectedNode.id.startsWith(excludedIdPrefix)) {
          setNotification({
            message: 'You cannot copy or paste the global node.',
            type: 'info',
          });
          return;
        }
      }
      if (selectedNode && event) {
        const reactFlowBounds =
          reactFlowWrapper?.current?.getBoundingClientRect();

        const position = reactFlowInstance?.project({
          x: event.clientX - (reactFlowBounds?.left || 0),
          y: event.clientY - (reactFlowBounds?.top || 0),
        });
        const newNode = {
          id: `${selectedNode.id}-copy`,
          type: selectedNode.type,
          label: selectedNode.data.label,
          position: position,
          data: {
            ...selectedNode.data,
            text: null,
            previewResponses: null,
            promptData: null,
            previewIndex: 0,
          },
        };

        onAddNode(newNode as any);
      } else {
        setNotification({
          message:
            'Something went wrong with pasting the node. Please try again.',
          type: 'error',
        });
      }
      setMenu({});
    },
    [selectedNode, setNotification, screenToFlowPosition, onAddNode],
  );

  const onNodeDragStop = useCallback(
    (node: Node) => {
      if (node?.parentNode) return;

      const overlappingGroups = nodes.filter(
        n =>
          n.type === 'nodesGroup' && isNodeOverlapping(n as any, node as any),
      );
      if (overlappingGroups.length > 0 && node.type !== 'nodesGroup') {
        const groupNode = overlappingGroups[0];

        const updatedNodes = nodes.map(n => {
          if (n.id === node.id) {
            return {
              ...n,
              position: {
                x: node?.position?.x - groupNode?.position?.x,
                y: node?.position?.y - groupNode?.position?.y,
              },
              data: {
                ...n?.data,
                collapsed: groupNode.data.collapsed,
              },
              parentNode: groupNode.id,
              extent: 'parent',
            };
          }
          return n;
        });
        setNodes(updatedNodes as any);
      }
    },
    [reactFlowInstance?.getIntersectingNodes, setNodes, nodes],
  );

  const handleClick = () => {
    if (shouldBackgroundClick) {
      const baseFormEdges = edges?.map(edg => {
        return {
          ...edg,
          selected: false,
          style: {
            ...edg.style,
            stroke: '#6e75ff',
            strokeWidth: 1,
          },
          type: edg.type === 'custom' ? 'custom' : '',
          markerEnd:
            edg.type === 'custom' ? undefined : coreEdgeStyle.markerEnd,
          data: {
            ...edg.data,
            coloredEdge: false,
          },
        };
      });
      setEdges(baseFormEdges);
    }
    setShouldBackgroundClick(true);
  };

  const handleVersionSelect = useCallback(
    (versionId: string) => {
      if (!flowId) return;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('versionId', versionId);
      searchParams.delete('historyId');

      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.location.href = newUrl;
    },
    [flowId],
  );

  const handleShowHistory = useCallback((versionId: string) => {
    setHistoryVersionId(versionId);
    setShowVersionHistory(true);
  }, []);

  const handleHideHistory = useCallback(() => {
    setHistoryVersionId('');
    setShowVersionHistory(false);
  }, []);

  const handleNavigateToProduction = useCallback(() => {
    if (!flowId) return;

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('versionId');
    searchParams.delete('historyId');

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.location.href = newUrl;
  }, [flowId]);

  const handleGoToEditing = useCallback(() => {
    if (!flowId) return;

    const allVersionsData = allVersions?.data;
    const nestedData =
      !Array.isArray(allVersionsData) &&
        allVersionsData &&
        typeof allVersionsData === 'object'
        ? Reflect.get(allVersionsData as object, 'data')
        : undefined;
    const items: FlowVersionSummaryDto[] = Array.isArray(allVersionsData)
      ? allVersionsData
      : Array.isArray(nestedData)
        ? nestedData
        : [];

    const mostRecent = items
      .filter(v => !v.isDeployed)
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )[0];

    if (mostRecent) {
      handleVersionSelect(mostRecent.id);
    }
  }, [flowId, allVersions, handleVersionSelect]);

  const handleAddNew = useCallback(() => {
    setShowVersionNameModal(true);
  }, []);

  const handleCreateVersion = useCallback(
    async (versionName: string) => {
      if (!flowId) return;
      const currentVersionId =
        new URLSearchParams(window.location.search).get('versionId') ||
        deployedVersion?.id ||
        '';
      try {
        await createVersionMutation.mutateAsync({
          flowId,
          data: {
            versionName,
            sourceVersionId: currentVersionId,
          },
        });
        setNotification({
          type: 'success',
          message: `Version "${versionName}" created successfully!`,
        });
        setShowVersionNameModal(false);
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error?.message || 'Failed to create version',
        });
      }
    },
    [flowId, createVersionMutation, deployedVersion?.id, setNotification],
  );

  const handlePushToProduction = useCallback(async () => {
    if (!flowId || !selectedVersionId) return;
    try {
      await pushToProductionMutation.mutateAsync({
        flowId,
        versionId: selectedVersionId,
      });
      setNotification({
        type: 'success',
        message: 'Successfully pushed to production!',
      });
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error?.message || 'Failed to push to production',
      });
    }
  }, [flowId, selectedVersionId, pushToProductionMutation, setNotification]);

  const handleRollback = useCallback(
    async (historyId: string) => {
      if (!flowId || !historyVersionId) return;
      try {
        await rollbackMutation.mutateAsync({
          flowId,
          versionId: historyVersionId,
          historyId,
        });
        setNotification({
          type: 'success',
          message: 'Successfully rolled back to previous version!',
        });
        setShowVersionHistory(false);
        setHistoryVersionId('');
        window.location.reload();
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error?.message || 'Failed to rollback',
        });
      }
    },
    [flowId, historyVersionId, rollbackMutation, setNotification],
  );

  const versionControlProps = useMemo(() => {
    if (!versionControlEnabled) {
      return undefined;
    }

    const formatDate = (date: Date | string | undefined) => {
      if (!date) return '-';
      try {
        const parsedDate = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(parsedDate.getTime())) return '-';
        return parsedDate.toLocaleDateString();
      } catch {
        return '-';
      }
    };

    const formatDateTime = (date: Date | string | undefined) => {
      if (!date) return '-';
      try {
        const parsedDate = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(parsedDate.getTime())) return '-';
        return parsedDate.toLocaleString();
      } catch {
        return '-';
      }
    };

    const liveVersion: VersionItem = deployedVersion
      ? {
        id: deployedVersion.id,
        name: deployedVersion.versionName,
        owner: user?.name || 'Unknown',
        lastModified: formatDate(deployedVersion.updatedAt),
        isLive: true,
        publishedInfo: `Published ${formatDate(deployedVersion.updatedAt)}`,
      }
      : {
        id: '',
        name: 'Not Published',
        owner: user?.name || 'Unknown',
        lastModified: '-',
        isLive: false,
        publishedInfo: 'No production version yet',
      };

    const allVersionsData = allVersions?.data;
    const nestedAllVersionsData =
      !Array.isArray(allVersionsData) &&
        allVersionsData &&
        typeof allVersionsData === 'object'
        ? Reflect.get(allVersionsData as object, 'data')
        : undefined;
    const allVersionItems: FlowVersionSummaryDto[] = Array.isArray(
      allVersionsData,
    )
      ? allVersionsData
      : Array.isArray(nestedAllVersionsData)
        ? nestedAllVersionsData
        : [];

    const versionHistoryData = versionHistory?.data;
    const nestedVersionHistoryData =
      !Array.isArray(versionHistoryData) &&
        versionHistoryData &&
        typeof versionHistoryData === 'object'
        ? Reflect.get(versionHistoryData as object, 'data')
        : undefined;
    const versionHistoryItems: VersionHistoryEntryDto[] = Array.isArray(
      versionHistoryData,
    )
      ? versionHistoryData
      : Array.isArray(nestedVersionHistoryData)
        ? nestedVersionHistoryData
        : [];

    const versions: VersionItem[] = allVersionItems.length
      ? [...allVersionItems]
        .filter(version => !version.isDeployed)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )
        .map(version => ({
          id: version.id,
          name: version.versionName || 'Unnamed Version',
          owner: user?.name || 'Unknown',
          lastModified: formatDate(version.updatedAt),
        }))
      : [];

    const historyVersion = allVersionItems.find(v => v.id === historyVersionId);
    const historyVersionName =
      historyVersion?.versionName ||
      (historyVersionId === deployedVersion?.id
        ? deployedVersion?.versionName
        : '') ||
      '';

    const historyEntries: VersionItem[] = versionHistoryItems.length
      ? versionHistoryItems
        .map(history => ({
          id: history.id,
          name: history.isCurrent
            ? historyVersionName ||
            history.versionName ||
            `Version ${formatDateTime(history.createdAt)}`
            : history.versionName ||
            `Version ${formatDateTime(history.createdAt)}`,
          owner: user?.name || 'Unknown',
          lastModified: formatDateTime(history.createdAt),
          isLive: history.isCurrent,
        }))
        .sort((a, b) => {
          if (a.isLive && !b.isLive) return -1;
          if (!a.isLive && b.isLive) return 1;
          return 0;
        })
      : [];

    return {
      selectedVersionId,
      onSelectVersion: handleVersionSelect,
      onShowHistory: handleShowHistory,
      onHideHistory: handleHideHistory,
      onNavigateToProduction: handleNavigateToProduction,
      onAddNew: handleAddNew,
      onPublish: handlePushToProduction,
      onGoToEditing: handleGoToEditing,
      liveVersion,
      versions,
      historyEntries,
      historyVersionName,
      onRestore: handleRollback,
      showHistory: showVersionHistory,
    };
  }, [
    versionControlEnabled,
    deployedVersion,
    allVersions,
    user?.name,
    showVersionHistory,
    versionHistory,
    selectedVersionId,
    historyVersionId,
    handleVersionSelect,
    handleShowHistory,
    handleHideHistory,
    handleNavigateToProduction,
    handleAddNew,
    handlePushToProduction,
    handleGoToEditing,
    handleRollback,
  ]);

  const setVersionControlProps = useVersionControlStore(
    state => state.setVersionControlProps,
  );
  const setIsProductionView = useVersionControlStore(
    state => state.setIsProductionView,
  );

  useEffect(() => {
    setVersionControlProps(versionControlProps || null);
  }, [versionControlProps, setVersionControlProps]);

  useEffect(() => {
    setIsProductionView(isProductionView);
  }, [isProductionView, setIsProductionView]);

  useEffect(() => {
    return () => {
      setVersionControlProps(null);
      setIsProductionView(false);
    };
  }, [setVersionControlProps, setIsProductionView]);

  return (
    <div className='dndflow' style={{ padding: '0' }}>
      {showOnboardingVideo && (
        <PopupWithVideo
          onClose={() => setShowOnboardingVideo(false)}
          videoUrl='https://youtu.be/UB8O7bIy8Oc'
        />
      )}

      <ReactFlowProvider>
        <LoopDetectionWrapper>
          {isLoading && <LoadingSpinner height='100vh' />}
          {!isLoading && nodes?.length !== 0 && (
            <div
              style={{
                width: '100%',
                height: '100%',
              }}
              className='reactflow-wrapper'
              data-flow-canvas-phase={flowCanvasPhase}
              ref={reactFlowWrapper}
              onContextMenu={onContextMenu}
              onClick={handleClick}
            >
              <ReactFlowBody
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                setReactFlowInstance={setReactFlowInstance}
                onConnect={onConnect}
                onEdgeDelete={onEdgeDelete}
                onEdgeUpdate={onEdgeUpdate}
                onEdgeUpdateStart={onEdgeUpdateStart}
                onEdgeUpdateEnd={onEdgeUpdateEnd}
                onDragOver={onDragOverExternal}
                onDrop={onDrop}
                onDragLeave={onDragLeaveExternal}
                updateNodeEdge={updateNodeEdge}
                setUpdateNodeEdge={setUpdateNodeEdge}
                onNodeDragStop={onNodeDragStop}
                user={user}
                onAddNode={onAddNode}
                isValidConnection={isValidConnection}
                onNodeSelected={handleSelectedNodeChange}
                handlePasteNode={handlePaste}
                handlePasteExternal={onPasteExternal}
                select={selectedNode}
                onDeleteNodes={handleDeleteNodes}
                readOnly={isProductionView}
              />

              {/* External file drag indicator */}
              <ExternalFileDragIndicator
                isDragging={externalDragState.isDragging}
                position={externalDragState.position}
                fileCount={externalDragState.fileCount}
              />

              {/* File options modal */}
              <PdfOptionsModal
                open={fileModalState.open}
                onClose={handleFileModalClose}
                onExtract={handleFileExtract}
                onView={handleFileView}
                fileName={fileModalState.fileName}
                fileType={fileModalState.fileType}
              />
            </div>
          )}
          <Sidebar
            onClickAddNode={onClickAddNode}
            onGenerateApi={onGenerateApi}
            flowId={flowId!}
            disabled={isProductionView}
          />
        </LoopDetectionWrapper>
      </ReactFlowProvider>

      {/*
      <Button
        onClick={onSave}
        variant="contained"
        sx={{
          position: 'absolute',
          top: '-37px',
          right: '20px',
          zIndex: '999',
          py: '5px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {'Reconnect all nodes'}
        <CheckCircle color="white" size={20} />
      </Button>
        <CheckCircle color='white' size={20} />
      </Button>
*/}

      <ModalComponent
        open={isModalOpen}
        onClose={() => {
          setIsModalOPen(false);
          let url = new URL(window.location.href);
          window.history.pushState({}, '', url.pathname);
        }}
      />
      <PaywallModal
        onHide={() => setOpenPaywall(false)}
        fullWidth={true}
        show={openPaywall}
        footer={
          <>
            <Button
              variant='outlined'
              sx={{
                color: '#3650DE',
                border: '1px solid #3650DE',
              }}
              onClick={() => {
                setOpenPaywall(false);
                setIsModalOPen(true);
                navigate('?pricing=true');
              }}
            >
              See All Plans
            </Button>
            <Button
              variant='contained'
              sx={{
                backgroundColor: '#3650DE',
                color: '#FFFFFF',
                outline: '1px solid #3650DE',
              }}
              onClick={() => {
                setOpenPaywall(false);
                setIsModalOPen(true);
                navigate(
                  `?pricing=true&proplan=${nextPlanAtLeastProfessional}`,
                );
              }}
            >
              Upgrade Now
            </Button>
          </>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <PremiumToolIcon />
            <Typography
              color='#101828'
              sx={{
                textAlign: 'center',
                fontFamily: 'Roboto',
                fontSize: '26px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '100%',
              }}
            >
              Premium Tool
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {/* <Typography
              color="#475467"
              sx={{
                textAlign: 'center',
                fontFamily: 'Roboto',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '175%',
                letterSpacing: '0.15px',
              }}
            >
              Premium Tool
            </Typography> */}
            <Typography
              color='#475467'
              sx={{
                textAlign: 'center',
                fontFamily: 'Roboto',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '126%',
              }}
            >
              The API Caller and MCP Server are premium tools. To access their
              functionality and more premium features, please upgrade your
              account.
            </Typography>
          </Box>
        </Box>
      </PaywallModal>
      {menu && (
        <ContextMenu
          id={menu.id as string}
          top={menu.top || 0}
          left={menu.left || 0}
          setMenu={setMenu}
          onCopy={() => console.log('Copying node')}
          onAddNode={onAddNode as any}
          onPaste={handlePaste}
          onDelete={() => console.log('Deleting node')}
          type={type}
          setType={setType}
        />
      )}

      <VersionNameModal
        show={showVersionNameModal}
        onHide={() => setShowVersionNameModal(false)}
        onSubmit={handleCreateVersion}
        title='Create New Version'
        label='Enter version name'
        placeholder='V 2.0'
        submitButtonText='Create'
      />

      <SavingStatus />
      <RemainingTokens />
    </div>
  );
};

export default memo(CoreNodeElement);
